'use client';

import React, { useCallback, useMemo } from 'react';
import { Check, EyeOff } from 'lucide-react';
import { cn, getEvColorClass } from '@sharpr/ui';
import { TeaseSurface } from '@sharpr/ui/rich-popover';
import { AnimatePresence } from 'framer-motion';
import { useSport } from '../../contexts/SportContext';
import { useEvTease } from '../../contexts/EvTeaseContext';
import type { SelectablePlayer } from '../../types/selection';
import { getMarketName, type MarketCode } from '@sharpr/types';
import { computeBestMarket } from '../../utils/bestLineCalculator';
import type { ResearchTarget, OverlayTarget } from '../../types/research';
import { buildResearchTarget } from '@/lib/research/buildResearchTarget';
import { PlayerAvatar } from '../PlayerAvatar';
import { TeamLogo } from '../TeamLogo';
import { NotesPopover } from '../NotesPopover';
import { BookThumbnail } from '../BookThumbnail';
import { PlayerOddsExpansion } from './PlayerOddsExpansion';
import { WeatherPill } from './weather/WeatherPill';
import { WeatherPillPlaceholder } from './weather/WeatherTrigger';
import type { GameWeather } from '@/services/weather/types';
import { formatEV, formatOdds } from '@sharpr/ui/format';

interface SelectablePlayerCardProps {
  // `isSelected` arrives as its own prop (below) — the card never reads it off the
  // player object, so the player type need not carry it.
  player: Omit<SelectablePlayer, 'isSelected'>;
  isSelected: boolean;
  onToggle: (id: string) => void;
  expandedCardId?: string | null;
  onToggleExpand?: (playerId: string) => void;
  selectedSportsbooks: string[];
  selectedMarkets: Set<MarketCode>;
  isQuestionable?: boolean;
  onOpenResearch?: (target: ResearchTarget) => void;
  onLineClick?: (target: OverlayTarget) => void;
  weather?: GameWeather | null;
  style?: React.CSSProperties;
  className?: string;
  /** When set, stamps `data-tour={tourTargetIds.X}` on the matching element so
   *  the product walkthrough can spotlight it. `playerName` → the research
   *  entry points: stamped on BOTH the avatar and the name (both open research),
   *  which the spotlight unions into one highlight;
   *  `firstBookButton` → mobile line-movement entry (inside the expansion);
   *  `cardRoot` → the whole card wrapper, used by the signed-out tour to point
   *  at "this row is what opens line movement after signing in";
   *  `lineupButton` → the lineup status chip (MLB; both viewports);
   *  `weatherButton` → the weather pill (MLB; mobile only — the desktop card has
   *  no weather pill, that lives in the selected-players table). */
  tourTargetIds?: {
    playerName?: string;
    firstBookButton?: string;
    cardRoot?: string;
    lineupButton?: string;
    weatherButton?: string;
  };
  /** The card's key in its list, and the callback told when its notes popover opens or closes (useAnchoredRow keeps
   *  the card mounted meanwhile). */
  notesKey: string;
  onNotesOpenChange?: (key: string, open: boolean) => void;
}

export const SelectablePlayerCard = React.memo(function SelectablePlayerCard({
  player,
  isSelected,
  onToggle,
  expandedCardId,
  onToggleExpand,
  selectedSportsbooks,
  selectedMarkets,
  isQuestionable,
  onOpenResearch,
  onLineClick,
  weather,
  style,
  className,
  tourTargetIds,
  notesKey,
  onNotesOpenChange,
}: SelectablePlayerCardProps) {
  const sport = useSport();
  const isExpanded = expandedCardId === player.id;
  const inQuestionableGame = player.scenario !== undefined;

  // Upstream recalculatePlayerBestLines already baked excludedFromBestLine into
  // every market's bestLines. When a market filter hides the pre-computed
  // bestMarket, recompute over just the filtered markets via the canonical
  // computeBestMarket (reads bestLines — never raw books — so exclusions hold).
  const bestMarket = useMemo(() => {
    if (selectedMarkets.size === 0) return player.bestMarket;
    if (player.bestMarket && selectedMarkets.has(player.bestMarket.market)) {
      return player.bestMarket;
    }
    const filtered = Object.fromEntries(
      Object.entries(player.markets).filter(([m]) => selectedMarkets.has(m as MarketCode)),
    );
    return computeBestMarket(filtered);
  }, [player.markets, player.bestMarket, selectedMarkets]);

  // Per-market teamNotes / lineup for two-way players (pitcher vs batter may differ)
  const bestMarketData = bestMarket?.market ? player.markets[bestMarket.market] : undefined;
  const displayTeamNotes = bestMarketData?.teamNotes ?? player.teamNotes;
  const displayLineup = bestMarketData?.lineup ?? player.lineup;

  const hasOdds = useMemo(() =>
    Object.values(player.markets).some(m => m.books && m.books.length > 0),
    [player.markets]);

  // Eye-slash on the best bet when its EV is admin-only. Gray = analyst PMF-hide
  // (blob-priced for admins; subscribers get odds-only); amber = hidden team /
  // hidden market (EV stripped for subscribers). Construction mode is denoted
  // by the page banner, not per-card.
  const bestBetHiddenBy = bestMarket && bestMarket.ev !== 0 ? bestMarketData?.hiddenBy : undefined;
  const bestBetAnalystHidden = !!bestBetHiddenBy?.includes('player');
  const bestBetAdminOnly = !!bestBetHiddenBy?.length;

  const bestBetInner = bestMarket && bestMarket.ev !== 0 ? (
    <>
      <div className="flex items-center gap-1 sm:gap-1.5">
        {(bestBetAnalystHidden || bestBetAdminOnly) && (
          <span
            title={bestBetAnalystHidden
              ? 'Analyst-hidden — EV visible to admins only; publishes odds-only to subscribers'
              : 'EV visible to admins only — stripped for subscribers'}
            className="inline-flex flex-shrink-0"
          >
            <EyeOff
              className={cn('h-3.5 w-3.5', bestBetAnalystHidden ? 'text-muted-foreground' : 'text-amber-500')}
              aria-label={bestBetAnalystHidden ? 'Analyst-hidden market' : 'Admin-only EV'}
            />
          </span>
        )}
        <span className={cn(
          "font-semibold text-xs sm:text-sm md:text-xs",
          bestMarket.line.isRemoved ? "text-muted-foreground" :
          getEvColorClass(bestMarket.ev, inQuestionableGame ? 'questionable' : 'default')
        )}>
          {formatEV(bestMarket.ev)}
        </span>
        <span className={cn(
          "uppercase text-xs",
          bestMarket.line.isRemoved ? "text-muted-foreground" : "text-muted-foreground dark:text-text-tertiary"
        )}>
          {getMarketName(bestMarket.market)}
        </span>
        <BookThumbnail book={bestMarket.line.book} size="sm" className="mr-0.5 md:mr-0" />
      </div>
      <div className="flex items-center gap-1 sm:gap-1.5">
        <span className={cn(
          "font-mono font-medium",
          bestMarket.line.isRemoved ? "text-muted-foreground" : "text-fg dark:text-text-primary"
        )}>
          {bestMarket.line.side === 'over' ? 'o' : 'u'}{bestMarket.line.line.toFixed(1)}
        </span>
        <span className="font-mono text-muted-foreground dark:text-text-secondary">
          ({formatOdds(bestMarket.line.odds)})
        </span>
      </div>
    </>
  ) : null;

  // Non-subscribers have no best-EV play (EV stripped server-side) — show the same
  // best-bet badge shape with only the EV% blurred. The market/book/line/odds are real
  // (odds survive the strip); we surface a representative line since there's no EV to
  // rank by.
  const teaseEv = useEvTease();

  const researchEnabled = !!onOpenResearch;

  // Representative market+line for the teaser. Only suppress when there's a REAL best
  // bet (ev ≠ 0); odds-only players carry a recomputed ev=0 fallback bestMarket, which
  // should still tease. No EV to rank by, so first market with a line (honoring the filter).
  const teaseBet = useMemo(() => {
    if (!teaseEv) return null;
    if (bestMarket && bestMarket.ev !== 0) return null;
    const hasFilter = selectedMarkets.size > 0;
    for (const [market, data] of Object.entries(player.markets)) {
      if (hasFilter && !selectedMarkets.has(market as MarketCode)) continue;
      const line = data.bestLines?.over ?? data.bestLines?.under ?? data.books?.find(b => !b.isRemoved);
      if (line && line.line != null) return { market: market as MarketCode, line };
    }
    return null;
  }, [teaseEv, bestMarket, player.markets, selectedMarkets]);

  // Tease: blur the ENTIRE best-bet badge (EV + market + book + line + odds) so non-subs
  // see there's a recommended play but can't read it. Blur applied per-row to keep the
  // two-div structure the desktop/mobile wrappers lay out.
  const teaseBlur = "blur-[3px] opacity-50 select-none";
  // No teaseBet means there is no representative live line to render.
  const bestBet = bestBetInner ?? (teaseEv && teaseBet ? (
    <TeaseSurface
      title="Unlock EV"
      surface="card_best_bet"
      body="See per-line edge over the market. Refreshed every minute, with model projections behind every number."
      ctaLabel="Start free trial"
      ctaHref="/signup"
      secondaryLabel="Already have an account? Log in"
      secondaryHref="/login"
      side="top"
      wrapperClassName="flex flex-col items-end gap-0.5 md:flex-row md:items-center md:gap-1.5"
    >
      <div className={cn("flex items-center gap-1 sm:gap-1.5", teaseBlur)}>
        <span className="font-semibold text-xs sm:text-sm md:text-xs text-muted-foreground">+0.0%</span>
        <span className="uppercase text-xs text-muted-foreground dark:text-text-tertiary">
          {getMarketName(teaseBet.market)}
        </span>
        <BookThumbnail book={teaseBet.line.book} size="sm" className="mr-0.5 md:mr-0" />
      </div>
      <div className={cn("flex items-center gap-1 sm:gap-1.5", teaseBlur)}>
        <span className="font-mono font-medium text-fg dark:text-text-primary">
          {teaseBet.line.side === 'over' ? 'o' : 'u'}{teaseBet.line.line.toFixed(1)}
        </span>
        <span className="font-mono text-muted-foreground dark:text-text-secondary">
          ({formatOdds(teaseBet.line.odds)})
        </span>
      </div>
    </TeaseSurface>
  ) : null);

  // Combined click handler for mobile (expand) and desktop (select)
  const handleCardClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    // Mobile: expansion (if has odds)
    if (window.innerWidth < 768 && hasOdds && onToggleExpand) {
      onToggleExpand(player.id);
    } else {
      // Desktop: selection
      onToggle(player.id);
    }
  }, [player.id, hasOdds, onToggleExpand, onToggle]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(player.id);
    }
  }, [player.id, onToggle]);

  // Shared research-open handler for the player name AND the avatar. When
  // research isn't enabled it no-ops and lets the click bubble to the card
  // (select/expand) instead of swallowing it.
  const handleOpenResearch = useCallback((e: React.MouseEvent) => {
    if (!researchEnabled || !onOpenResearch) return;
    e.preventDefault();
    e.stopPropagation();
    onOpenResearch(buildResearchTarget(player, { bestMarket }));
  }, [researchEnabled, onOpenResearch, player, bestMarket]);

  // Keyboard equivalent so the name/avatar (role=button) are operable without a
  // pointer (WCAG 2.1.1). stopPropagation keeps Enter/Space from also toggling
  // the enclosing card button.
  const handleResearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!researchEnabled || !onOpenResearch) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onOpenResearch(buildResearchTarget(player, { bestMarket }));
    }
  }, [researchEnabled, onOpenResearch, player, bestMarket]);

  return (
    <div style={style} data-tour={tourTargetIds?.cardRoot} className={cn("w-full", className)}>
      <button
        type="button"
        className={cn("w-full text-left", hasOdds ? "md:pointer-events-auto" : "pointer-events-none md:pointer-events-auto")}
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        aria-pressed={isSelected}
        aria-expanded={isExpanded}
        aria-label={`${player.name}${player.position ? ` - ${player.position}` : ''} - ${isSelected ? 'Selected' : 'Not selected'}. ${bestMarket ? `Best bet: ${formatEV(bestMarket.ev)} EV on ${bestMarket.market}` : 'No bets available'}`}
      >
      <div
        className={cn(
          "transition-[color,background-color,transform] duration-150 md:cursor-pointer relative rounded",
          "focus-within:bg-gray-50 dark:focus-within:bg-bg-tertiary",
          isSelected && "bg-brand-50/30 dark:bg-brand-600/10",
          "md:hover:bg-gray-50 md:dark:hover:bg-bg-tertiary",
          // Mobile tap feedback — quick press tint + 1% scale. Neutralized on
          // desktop (the row selects there) and for reduced-motion users.
          "active:bg-gray-100 dark:active:bg-bg-tertiary",
          "active:scale-[0.99] md:active:scale-100 motion-reduce:active:scale-100"
        )}
      >
        {/* Selection indicator (Desktop Only) */}
        <div className={cn(
          "absolute top-3 right-3 w-5 h-5 rounded-full transition-all duration-150",
          "hidden md:flex items-center justify-center",
          isSelected ? [
            "bg-brand-500 dark:bg-brand-600",
          ] : [
            "bg-gray-200/60 dark:bg-border-primary",
          ]
        )}>
          {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
        </div>

        <div className="pl-2.5 pr-3.5 py-2 sm:pl-4 md:pr-5 sm:py-2.5 md:pl-4 md:py-2.5">
          <div className="md:pr-8"> {/* Space for checkbox on desktop */}
            {/* Container: Horizontal on mobile, Vertical on desktop */}
            <div className="flex flex-row md:flex-col gap-2 sm:gap-3 md:gap-0">
              {/* Player Info */}
              <div className="flex items-start md:items-center gap-2 sm:gap-3 flex-1 min-w-0">
                <span
                  data-tour={tourTargetIds?.playerName}
                  role={researchEnabled ? 'button' : undefined}
                  tabIndex={researchEnabled ? 0 : undefined}
                  aria-label={researchEnabled ? `Open ${player.name} research` : undefined}
                  onClick={handleOpenResearch}
                  onKeyDown={researchEnabled ? handleResearchKeyDown : undefined}
                  className={cn('flex-shrink-0', researchEnabled && 'cursor-pointer')}
                >
                  <PlayerAvatar name={player.name} imageUrl={player.imageUrl} size="md" className="w-10 h-10 sm:w-11 sm:h-11" />
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-xs sm:text-sm text-fg dark:text-text-primary flex items-center gap-1 min-w-0">
                    <span
                      data-tour={tourTargetIds?.playerName}
                      className={cn(
                        // py/-my expands the touch target to >=24px (WCAG 2.5.8)
                        // without altering layout; mobile-only (desktop selects the row).
                        "truncate flex-shrink py-1.5 -my-1.5 md:py-0 md:my-0",
                        researchEnabled && "hover:text-brand-500 dark:hover:text-brand-400 cursor-pointer transition-colors"
                      )}
                      onClick={handleOpenResearch}
                      onKeyDown={researchEnabled ? handleResearchKeyDown : undefined}
                      role={researchEnabled ? 'button' : undefined}
                      tabIndex={researchEnabled ? 0 : undefined}
                    >
                      {player.name}
                    </span>
                    {isQuestionable && (
                      <span className="inline-flex items-center flex-shrink-0 ml-1 px-1 py-0.5 text-[10px] font-bold leading-none rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 align-middle">
                        Q
                      </span>
                    )}
                    {player.position && (
                      <span className="text-xs sm:text-sm font-normal text-muted-foreground dark:text-text-tertiary flex-shrink-0 whitespace-nowrap">
                        {'• '}
                        {player.position}
                      </span>
                    )}
                    <NotesPopover lineup={displayLineup} teamNotes={displayTeamNotes} playerNotes={player.playerNotes} teamLabel={player.team} sport={sport}
                      playerName={player.name} playerImageUrl={player.imageUrl} onOpenChange={open => onNotesOpenChange?.(notesKey, open)} className="align-middle" dataTour={tourTargetIds?.lineupButton} />
                  </h3>
                  {/* Matchup row — team/opponent + first-pitch time, display-only.
                      Game lines now live in the player details overlay, so this
                      is no longer an interactive trigger. */}
                  <div className="flex items-center gap-0.5 sm:gap-1 md:gap-1.5 mt-2 md:mt-1.5 text-xs md:text-xs text-muted-foreground dark:text-text-secondary">
                    {player.game ? (
                      <span className="inline-flex items-center gap-0.5 sm:gap-1 md:gap-1.5">
                        <TeamLogo team={player.team} sport={sport} className="w-4 h-4 md:w-3.5 md:h-3.5" />
                        <span className="hidden md:inline flex-shrink-0">{player.team}</span>
                        <span className="mx-px sm:mx-0.5 md:mx-1 flex-shrink-0">{player.game.isHome ? 'vs' : '@'}</span>
                        <TeamLogo team={player.game.opp} sport={sport} className="w-4 h-4 md:w-3.5 md:h-3.5" />
                        <span className="hidden md:inline flex-shrink-0">{player.game.opp}</span>
                        {player.game.commenceTime && (() => {
                          const d = new Date(player.game.commenceTime);
                          const isToday = d.toLocaleDateString() === new Date().toLocaleDateString();
                          const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                          return (
                            <span className="flex-shrink-0 ml-0.5 text-[11px] md:text-xs text-muted-foreground dark:text-gray-300 whitespace-nowrap">
                              · {isToday ? time : `${d.getMonth() + 1}/${d.getDate()} ${time}`}
                            </span>
                          );
                        })()}
                      </span>
                    ) : (
                      <>
                        <TeamLogo team={player.team} sport={sport} className="w-3.5 h-3.5" />
                        <span className="hidden md:inline flex-shrink-0">{player.team}</span>
                      </>
                    )}
                    {/* Weather slot (MLB mobile). Weather arrives via a SEPARATE async
                        fetch (useMlbWeather, ['mlb-weather']) after first paint, so we
                        hold a same-height placeholder until the real pill is available —
                        and for indoor venues, which never get a pill — so the pill's
                        arrival is height-neutral. Eliminates the mobile /mlb CLS
                        (0.149 vs NBA 0.043) with no magic min-height: the placeholder
                        shares the pill's size classes, so it can't drift. */}
                    {player.game && sport === 'mlb' && (
                      weather && !weather.indoorVenue ? (
                        // Wrapper carries the tour target so the walkthrough can
                        // spotlight the pill (mobile only — md:hidden mirrors the
                        // pill's own visibility, and gives a real box to measure).
                        <span data-tour={tourTargetIds?.weatherButton} className="inline-flex md:hidden">
                          <WeatherPill
                            weather={weather}
                            size="xs"
                            // Visible pill stays compact; the wrapper span (the actual
                            // tap target) gets an invisible ~16px vertical hit-area
                            // expansion via a pseudo-element so it clears the 24px
                            // touch-target floor without enlarging the pill.
                            wrapperClassName="inline-flex items-center md:hidden ml-1 relative before:absolute before:content-[''] before:-inset-y-2 before:-inset-x-1"
                          />
                        </span>
                      ) : (
                        <WeatherPillPlaceholder size="xs" className="md:hidden ml-1" />
                      )
                    )}
                  </div>

                  {/* Desktop: Best Bet under game info */}
                  {bestBet && (
                    <div className={cn(
                      "hidden md:flex flex-row items-center gap-2 text-xs mt-1.5",
                      bestMarket?.line.isRemoved && "opacity-50",
                    )}>
                      {bestBet}
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile: Best Bet right-aligned */}
              {bestBet && (
                <div className={cn(
                  "md:hidden flex-shrink-0 flex flex-col items-end justify-between gap-0.5",
                  bestMarket?.line.isRemoved && "opacity-50",
                )}>
                  <div className="flex flex-col items-end gap-2 text-xs">
                    {bestBet}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </button>

      {/* Expansion Content (Mobile Only) */}
      <AnimatePresence>
        {hasOdds && isExpanded && (
          <div className="md:hidden">
            <PlayerOddsExpansion
              player={player}
              selectedSportsbooks={selectedSportsbooks}
              onLineClick={onLineClick}
              noProjection={bestMarket?.ev === 0 || !bestMarket}
              selectedMarkets={selectedMarkets}
              sport={sport}
              tourTargetIds={tourTargetIds?.firstBookButton ? { firstBookButton: tourTargetIds.firstBookButton } : undefined}
            />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
});
